import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import ProvidersTypes from 'common/enums/providersTypes';
import { getDynamicFields, getNestedData } from 'common/features/renderForms/utils';
import { getProvider } from 'redux/slices/organizationSlice';
import YesNoDialog from '../../../common/components/YesNoDialog';
import useStylesForm from '../../../common/components/styles/useStylesForm.styles';
import { useFormFields } from '../../../libs/hooksLib';
import { getAssets, resetAssets } from '../../../redux/slices/assetSlice';
import {
    cancelCloseCustomDialogAction,
    closeCustomDialogAction,
    getIsDialogOnClose,
} from '../../../redux/slices/customDialogSlice';
import useStylesProductForm from '../styles/useStylesProductForm.styles';
import ProductFormConfiguration from './ProductFormConfiguration';
import ProductFormNav from './ProductFormNav';
import ProductFormVersions from './ProductFormVersions';

const ProductForm = (props) => {
    const { action, product, onSubmit, readOnly, formConfigProduct, formConfigProductVariant, canEdit, onCancel } = props;

    const { classes: classes1 } = useStylesForm();
    const { classes: classes2, cx } = useStylesProductForm();
    const classes = { ...classes1, ...classes2 };

    const { t } = useTranslation();
    const dispatch = useDispatch();

    const assets = useSelector(getAssets);
    const provider = useSelector(getProvider);
    const [step, setStep] = useState(1);
    const isDrawerOnClose = useSelector(getIsDialogOnClose);
    const [variants, setVariants] = useState(product.options?.length > 0 ? product.variants : []);
    const [options, setOptions] = useState(product.options);
    const [openCancelDlg, setOpenCancelDlg] = useState(false);
    const cancelConfirmTitle = t('product.productForm.cancelConfirm');
    const cancelConfirmMessage = t('product.productForm.cancelMessage');

    const initialForm = useMemo(() => {
        const initial = {
            externalId:
                provider?.type === ProvidersTypes.ONLIVE && product.externalId === product.id
                    ? null
                    : product.externalId,
            images: product.images,
            category: product.category ? product.category : 'demo',
            name: product.name,
            description: product.description,
            descriptionHtml: product.descriptionHtml,
            enableVariants:
                product.options.length > 0 &&
                !(product.options[0].name === 'Title' && product.options[0].values?.[0] === 'Default Title'),
            tags: product.tags,
            options: product.options,
            variants: product.variants,
            discountType: product.discountType ? product.discountType : 'none',
            discount: product.discount,
            currency: product.variants.length > 0 ? product.variants[0].currency : 'EUR',
            sku: product.sku,
            gtin: product.gtin,
            extraFields: product?.extraFields,
        };
        const dynamic = canEdit ? getDynamicFields(formConfigProduct, product?.extraFields) : {};
        return { ...initial, ...dynamic };
    }, [product, formConfigProduct, canEdit, provider]);

    const [fields, handleFieldChange, resetProductForm] = useFormFields(initialForm);

    const initialProductVarianForm = useMemo(() => {
        const variant = product.options.length === 0 && product.variants.length > 0 ? product.variants[0] : {};

        const initial = {
            externalId: variant?.externalId ?? '0',
            url: variant?.url ?? '',
            images: [],
            currency: fields.currency,
            price: variant?.price ?? undefined,
            stock: variant?.stock ?? undefined,
            selectedOptions: [{ name: 'Title', value: 'Default Title' }],
            sku: variant?.sku ?? '',
            gtin: variant?.gtin ?? '',
            broken: variant?.broken ?? false,
            brokenUrl: variant?.broken ? variant?.url : undefined,
        };
        return { ...initial };
    }, [product, fields.currency]);

    const [productVariantFields, handleProductVariantFieldChange, resetProductVarianForm] =
        useFormFields(initialProductVarianForm);

    const initialDescriptionForm = useMemo(
        () => ({
            descriptionHtml: product.descriptionHtml,
        }),
        [product],
    );

    const [descriptionFields, handleDescriptionChange, resetDescriptionForm] = useFormFields(initialDescriptionForm);

    const restart = useCallback(() => {
        resetProductForm(initialForm);
        resetProductVarianForm(initialProductVarianForm);
        resetDescriptionForm(initialDescriptionForm);
        dispatch(resetAssets());
    }, [
        dispatch,
        initialDescriptionForm,
        initialForm,
        initialProductVarianForm,
        resetDescriptionForm,
        resetProductForm,
        resetProductVarianForm,
    ]);

    const finalCancel = useCallback(() => {
        restart();
        if (onCancel) {
            try {
                onCancel();
            } catch (error) {
                console.error('Error calling product form onCancel callback:', error);
            }
        }
        dispatch(closeCustomDialogAction());
        // eslint-disable-next-line
    }, [dispatch, restart]);

    const userDidChange = useCallback(() => {
        if (readOnly) {
            return false;
        } else {
            return (
                JSON.stringify(initialForm) !== JSON.stringify(fields) ||
                JSON.stringify(fields.images) !== JSON.stringify(assets)
            );
        }
    }, [assets, fields, initialForm, readOnly]);

    const manageCancel = useCallback(() => {
        if (!userDidChange()) {
            finalCancel();
        }
        setOpenCancelDlg(true);
    }, [finalCancel, userDidChange]);

    useEffect(() => {
        if (isDrawerOnClose) {
            manageCancel();
        }
    }, [isDrawerOnClose, manageCancel]);

    const cancelDlg = () => {
        setOpenCancelDlg(false);
        dispatch(cancelCloseCustomDialogAction());
    };

    const cancel = (event) => {
        event.preventDefault();
        manageCancel();
    };

    function handleSubmitProduct() {
        onSubmit(
            {
                ...fields,
                extraFields:
                    canEdit && formConfigProduct ? getNestedData(formConfigProduct, fields) : fields.extraFields,
                images: assets,
                descriptionHtml: descriptionFields.descriptionHtml,
                options: [],
                variants: [
                    {
                        ...productVariantFields,
                        images: assets,
                    },
                ],
            },
            restart,
        );
    }

    function handleSubmitVariant() {
        onSubmit(
            {
                ...fields,
                extraFields:
                    canEdit && formConfigProduct ? getNestedData(formConfigProduct, fields) : fields.extraFields,
                options,
                descriptionHtml: descriptionFields.descriptionHtml,
                variants,
            },
            restart,
        );
    }

    const onStep = (s) => {
        setStep(s);
    };

    const handleProductCurrencyFieldChange = (event) => {
        const value = event.target.value;
        handleFieldChange({
            target: {
                name: 'currency',
                value,
            },
        });
        handleProductVariantFieldChange({
            target: {
                name: 'currency',
                value,
            },
        });
        let newVariants = [];
        variants.forEach((v) => {
            let variant = {
                ...v,
                currency: value,
            };
            newVariants = [...newVariants, variant];
        });
        setVariants(newVariants);
    };

    return (
        <div className={cx(classes.root, classes.h100vh)}>
            <div className={classes.formContainer}>
                <div className={classes.headerContainer}>
                    <div className={classes.header}>
                        <div className={classes.headerTop}>
                            <span className={classes.title}>{action}</span>
                        </div>
                        <ProductFormNav step={step} setStep={setStep} enableVariants={fields.enableVariants} />
                    </div>
                </div>

                <div className={cx(classes.content, classes.contentSeparator)}>
                    {
                        {
                            1: (
                                <ProductFormConfiguration
                                    readOnly={readOnly}
                                    fields={fields}
                                    handleFieldChange={handleFieldChange}
                                    step={step}
                                    descriptionFields={descriptionFields}
                                    handleDescriptionChange={handleDescriptionChange}
                                    productVariantFields={productVariantFields}
                                    handleProductVariantFieldChange={handleProductVariantFieldChange}
                                    handleProductCurrencyFieldChange={handleProductCurrencyFieldChange}
                                    setStep={onStep}
                                    onSubmit={handleSubmitProduct}
                                    cancel={cancel}
                                    canEdit={canEdit && formConfigProduct}
                                    formConfigProduct={formConfigProduct}
                                />
                            ),
                            2: (
                                <ProductFormVersions
                                    readOnly={readOnly}
                                    step={step}
                                    setStep={onStep}
                                    options={options}
                                    setOptions={setOptions}
                                    variants={variants}
                                    setVariants={setVariants}
                                    fields={fields}
                                    onSubmit={handleSubmitVariant}
                                    cancel={cancel}
                                    canEdit={canEdit && formConfigProductVariant}
                                    formConfigProductVariant={formConfigProductVariant}
                                />
                            ),
                        }[step]
                    }
                </div>
            </div>
            <YesNoDialog
                openDialog={openCancelDlg}
                title={cancelConfirmTitle}
                messageToConfirm={cancelConfirmMessage}
                yesButtonCallback={() => {
                    finalCancel();
                }}
                noButtonCallback={() => {
                    cancelDlg();
                }}
            />
        </div>
    );
};

export default ProductForm;
